import React from 'react';
import { Sankey, ResponsiveContainer, Tooltip } from 'recharts';

const NODE_COLORS = {
  'Saved Jobs': '#6366f1',
  'Applied': '#3b82f6',
  'Interviewing': '#f59e0b',
  'Offered': '#10b981',
  'Rejected (Early)': '#fb7185',
  'Rejected (Late)': '#e11d48',
  'Ghosted': '#9ca3af',
};

const renderNode = (props) => {
  const { x, y, width, height, payload } = props;
  const fill = NODE_COLORS[payload.name] ?? '#5a7a75';
  const value = payload.value ?? 0;

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} rx={3} stroke="none" />
      <text
        x={x + width + 8}
        y={y + height / 2}
        dominantBaseline="middle"
        fill="#374151"
        fontSize={11}
        fontWeight={600}
      >
        {value} {payload.name}
      </text>
    </g>
  );
};

const SankeyTooltip = ({ active, payload, nodes }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload ?? payload[0];
  const value = item?.value ?? 0;
  const src = item?.source;
  const tgt = item?.target;
  const srcName = typeof src === 'object' ? src.name : nodes?.[src]?.name ?? '';
  const tgtName = typeof tgt === 'object' ? tgt.name : nodes?.[tgt]?.name ?? '';
  return (
    <div className="bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm text-sm text-gray-800">
      {srcName} → {tgtName}: <strong>{value}</strong>
    </div>
  );
};

const ConversionBar = ({ conversion }) => {
  const items = [
    { label: 'Save → Apply', value: conversion?.saveToApply ?? 0 },
    { label: 'Apply → Interview', value: conversion?.applyToInterview ?? 0 },
    { label: 'Interview → Offer', value: conversion?.interviewToOffer ?? 0 },
    { label: 'Overall', value: conversion?.overall ?? 0 },
  ];
  return (
    <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-100">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">{item.label}:</span>
          <span className="font-bold text-gray-800">{(item.value * 100).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
};

const AnalyticsSankey = ({ data }) => {
  const safeData = data || { nodes: [], links: [], totals: {}, conversion: {} };
  const filteredLinks = (safeData.links || []).filter(l => l.source !== l.target);
  const sankeyInput = { nodes: safeData.nodes || [], links: filteredLinks };
  const totals = safeData.totals || {};
  const hasFlow = filteredLinks.length > 0 && (sankeyInput.nodes || []).length > 0;

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden p-5 border-t-4 border-indigo-500 h-full flex flex-col">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-900">Application Flow</h3>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400 inline-block" /> Ghosted = no response &gt;21 days</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        How your {totals.saved ?? 0} saved jobs flow through the pipeline
      </p>

      {hasFlow ? (
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <Sankey
              data={sankeyInput}
              node={renderNode}
              link={{ stroke: '#94a3b8', fill: '#e2e8f0', fillOpacity: 0.5 }}
              nodePadding={18}
              nodeWidth={14}
              linkCurvature={0.5}
              margin={{ top: 8, right: 120, left: 8, bottom: 8 }}
            >
              <Tooltip content={<SankeyTooltip nodes={sankeyInput.nodes} />} />
            </Sankey>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="py-10 text-center text-xs text-gray-400">
          Save jobs and submit applications to see your pipeline flow.
        </div>
      )}

      <ConversionBar conversion={safeData.conversion || {}} />
    </div>
  );
};

export default AnalyticsSankey;
